/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect, useState } from 'react';
import useDispatchRegistrationState from '../../stores/DispatchRegistrationState';
import useDispatchState from '../../stores/DispatchState';
import ListRegistrations from '../DispatchRegistrationTab/components/ListRegistrations';
import { useLang } from '~/hooks/useLang';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { truncate } from '../../utils/stringsManipulations';
import useDispatchRegistrationOrganState, { organDefaultState } from '../../stores/DispatchRegistrationOrganState';
import OrgansForm from './components/OrgansForm';
import { Tooltip } from '@mui/material';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { saveLogVisualizeQualificators } from '../../services';
import { IOrganDTO, IRawOrgan } from '../../interfaces/IDispacthRegistrationOrgan';
import useSocket from '~/hooks/useSocket';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import notify from '~/utils/notify';
import { toDomain } from '~/mappers/registration-organ-mapper';
import useSystemState from '~/features/System/stores/SystemState';

interface IOrgans {
  readonly handleClickOpenConfirmAction: (actionName: string) => void;
}

interface IMessageOrgans {
  readonly content: IRawOrgan
  readonly event_type: string
}


export default function Organs({ handleClickOpenConfirmAction }: IOrgans) {
  const { translate } = useLang();
  const { socket } = useSocket();
  const { windowManager } = useSystemState();

  const { dispatch } = useDispatchState();
  const { occurrence, status, id: dispatchIdState } = dispatch();
  const { id: occurrenceIdState, } = occurrence;

  const { registrationDispatch, } = useDispatchRegistrationState();
  const { addRegistration, loadingRegistrations, registrationRowId, deleteRegistration } = registrationDispatch();

  const { registrationOrganDispatch, getAllAssociatedOrgans, deleteOrgans } = useDispatchRegistrationOrganState();
  const { organs, organ } = registrationOrganDispatch();
  const [rows, setRows] = useState<readonly IOrganDTO[]>([])

  const columns = ([
    {
      field: 'id',
    },
    {
      field: 'auxiliaryOrgans',
      headerName: translate('Organization'),
      flex: 1,
      valueFormatter: (row) => {
        return row ? row['name'] : '---';
      }
    },
    {
      field: 'actionTaken',
      headerName: translate('Action Taken'),
      flex: 1,
      valueFormatter: (row) => {
        return row ? row['name'] : '---';
      }
    },
    {
      field: 'notes',
      headerName: translate('Note'),
      flex: 1,
      renderCell: (params) => {
        if (!params.value) {
          return <p>
            ----
          </p>
        }
        return <p>
          {truncate(params.value, 65)}
        </p>
      }
    },
    {
      field: 'actions', type: 'actions', flex: 0.5,
      getActions: (params) => [
        status.value.id === 8 ? <Tooltip key={2} title={translate('View Record')}>
          <span>
            <GridActionsCellItem
              key={params.id}
              icon={<RemoveRedEyeIcon />}
              onClick={() => {
                saveLogVisualizeQualificators(params.row.id, '/occurrence/associated-organization/generate-view-timeline').then((response) => {
                  if (response.status === 200) {
                    organ.set(params.row);
                    addRegistration.set(true)
                  }
                })
              }}
              label="Print" />
          </span>
        </Tooltip> : <></>,
        status.value.id !== 8 ? <Tooltip key={2} title={translate('Edit Record')}>
          <span>
            <GridActionsCellItem
              key={params.id}
              icon={<EditIcon />}
              onClick={() => {
                organ.set(params.row);
                addRegistration.set(true)
              }}
              label="Print" />
          </span>
        </Tooltip> : <></>,
        status.value.id !== 8 ? <Tooltip key={2} title={translate('Delete Record')}>
          <span>
            <GridActionsCellItem
              key={params.id}
              icon={<DeleteIcon />}
              onClick={() => {
                registrationRowId.set(params.row.id);
                handleClickOpenConfirmAction('delete')
              }}
              label="Print" />
          </span>
        </Tooltip> : <></>,
      ]
    },
  ])

  const handleAdd = () => {
    addRegistration.set(true)
    organ.set({
      ...organDefaultState,
      occurrenceId: occurrenceIdState.get()
    })
  }

  const handleDelete = useCallback(async (id: string) => {
    loadingRegistrations.set(true)
    await deleteOrgans(id)
    notify({ message: translate('Successfully Deleted'), type: 'success' })
    loadingRegistrations.set(false)
    deleteRegistration.set(false)
  }, [deleteOrgans, deleteRegistration, loadingRegistrations, translate])

  useEffect(() => {
    if (deleteRegistration.get()) {
      handleDelete(registrationRowId.get())
    }
  }, [deleteRegistration, handleDelete, registrationRowId])


  useEffect(() => {
    if (windowManager()['registration'].open.get() && occurrenceIdState.get()) {
      loadingRegistrations.set(true);
      getAllAssociatedOrgans(occurrenceIdState.get())
        .then((res) => {
          setRows(res)
          organs.set(res)
        })
        .finally(() => {
          loadingRegistrations.set(false)
        })
    }
  }, [occurrenceIdState.get()]);

  const newMessage = useCallback((message: IMessageOrgans) => {
    if (message.event_type === 'OCCURRENCE_ASSOCIATED_ORGANIZATION_CREATE') {
      if (message.content.dispatch_id !== dispatchIdState.get()) {
        return
      }
      setRows(prev => [...prev, toDomain(message.content)])
    }
    if (message.event_type === 'OCCURRENCE_ASSOCIATED_ORGANIZATION_DELETE') {
      const filterRows = rows.filter(row => row.id !== message.content.id)
      setRows(filterRows);
    }
    if (message.event_type === 'OCCURRENCE_ASSOCIATED_ORGANIZATION_CHANGE') {
      const updateRows = rows.map(row => {
        if (row.id === message.content.id) {
          return toDomain(message.content)
        } else {
          return row
        }
      })
      setRows(updateRows);
    }
  }, [rows])

  useEffect(() => {
    socket.on('occurrences', newMessage)
    return () => {
      socket.off('occurrences', newMessage)
    }
  }, [newMessage, socket])

  return (
    addRegistration.get() ? (
      <OrgansForm
        handleClickOpenConfirmAction={handleClickOpenConfirmAction}
      />
    ) : (
      <ListRegistrations
        handleAdd={handleAdd}
        rows={rows}
        columns={columns as GridColDef[]}
      />
    )
  )
};